import { IAnchorLink } from '@models/IAnchorLink';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import { useCallback, useEffect, useState } from 'react';
import useDebouncedValue from './useDebouncedValue';
import useMediaQuery from './useMediaQuery';

interface Props {
  readonly items: IAnchorLink[];
  readonly stickToBottom?: boolean;
}

const useAnchorNavScrolling = ({ items, stickToBottom }: Props) => {
  const isMobileViewport = useMediaQuery(1024);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;
  const [activeAnchorId, setActiveAnchorId] = useState('');
  const debouncedActiveAnchorId = useDebouncedValue(activeAnchorId, 250);

  const getScrollTopOffset = () => {
    const anchorMenu = document.getElementById('AnchorMenu');
    const productNavigation = document.getElementById('ProductNavigation');
    const siteNotifications = document.getElementById('SiteNotifications');

    const anchorMenuHeight =
      anchorMenu && getComputedStyle(anchorMenu).position === 'sticky'
        ? anchorMenu.clientHeight
        : 0;
    const productNavigationHeight =
      productNavigation &&
      !stickToBottom &&
      getComputedStyle(productNavigation).position === 'sticky'
        ? productNavigation.clientHeight
        : 0;
    const siteNotificationsHeight =
      siteNotifications?.parentElement?.parentElement &&
      getComputedStyle(siteNotifications.parentElement?.parentElement)
        .position === 'sticky'
        ? siteNotifications.clientHeight
        : 0;

    const yOffset =
      -20 -
      anchorMenuHeight -
      productNavigationHeight -
      siteNotificationsHeight;

    return yOffset;
  };

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;

    const lazyLoadedImages = Array.from(
      document.querySelectorAll('img[loading="lazy"]')
    ) as HTMLImageElement[];
    const imagesBeforeElement = lazyLoadedImages.filter(
      (image) =>
        element.compareDocumentPosition(image) ===
        Node.DOCUMENT_POSITION_PRECEDING
    );
    imagesBeforeElement.forEach((image) => {
      image.setAttribute('loading', 'eager');
    });

    // Need some timeout to allow for the images to load and adjust the page scrolling
    const scrollTimeout = (() => {
      if (imagesBeforeElement.length === 0) return 0;
      return isMobileViewport ? 400 : 300;
    })();
    setTimeout(() => {
      const scrollTopPosition =
        (element?.getBoundingClientRect().top || 0) +
        window.scrollY +
        getScrollTopOffset();

      window.scrollTo({ top: scrollTopPosition, behavior: 'smooth' });
    }, scrollTimeout);
  };

  const filteredIds = useCallback(
    () => items.map((item) => `#a-${item.anchor}`),
    [items]
  );

  const updateActiveSection = useCallback(() => {
    const filterIds = filteredIds();

    if (filterIds.length === 0) return;

    const elements = document.querySelectorAll(filterIds.toString());

    const observer = new IntersectionObserver(
      (entries) => {
        const listOfCurrentlyIntersecting = entries.filter(
          (entry) => entry.isIntersecting
        );

        let chosenItem: IntersectionObserverEntry | undefined;

        if (listOfCurrentlyIntersecting.length === 1) {
          chosenItem = listOfCurrentlyIntersecting.at(0);
        }

        if (listOfCurrentlyIntersecting.length > 1) {
          let found: IntersectionObserverEntry | undefined;
          listOfCurrentlyIntersecting.forEach((el) => {
            if (!found) found = el;
            if (el.intersectionRatio >= found?.intersectionRatio) found = el;
          });

          chosenItem = found;
        }

        setActiveAnchorId(chosenItem?.target.id.split('-')[1] || '');
      },
      {
        rootMargin: '-30% 0% -30% 0%',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
  }, [filteredIds]);

  useEffect(() => {
    window.addEventListener('scroll', updateActiveSection);
    updateActiveSection();

    return () => window.removeEventListener('scroll', updateActiveSection);
  }, [updateActiveSection]);

  useEffect(() => {
    // Remove the stickyness on the parent containers if zoomed.
    ['ProductNavigation', 'AnchorMenu'].forEach((stickyNav) =>
      document
        .getElementById(stickyNav)
        ?.classList[isZoomedDesktop ? 'remove' : 'add']('sticky')
    );
  }, [isZoomedDesktop]);

  return {
    scrollTo,
    activeAnchorId: debouncedActiveAnchorId,
  };
};

export default useAnchorNavScrolling;
